/**
 * TermsAcceptance Component
 *
 * Checkbox component for accepting terms and conditions before payment.
 * Includes links to privacy policy and terms of use pages that open in new tabs.
 *
 * @validates Requirements 7.1, 7.4
 */

import React from 'react';

/**
 * Links to privacy policy and terms of use pages
 */
const TERMS_LINKS = {
  privacy: '/privacy',
  terms: '/terms',
};

/**
 * Props for the TermsAcceptance component
 */
export interface TermsAcceptanceProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Callback when the checkbox state changes */
  onChange: (checked: boolean) => void;
  /** Whether the checkbox is disabled (e.g., during payment processing) */
  disabled?: boolean;
}

/**
 * TermsAcceptance Component
 *
 * Displays a checkbox for accepting terms and conditions with links to
 * privacy policy and terms of use pages.
 *
 * @example
 * ```tsx
 * <TermsAcceptance
 *   checked={termsAccepted}
 *   onChange={setTermsAccepted}
 *   disabled={isProcessing}
 * />
 * ```
 */
export function TermsAcceptance({
  checked,
  onChange,
  disabled = false,
}: TermsAcceptanceProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <div className="flex items-start gap-2">
      <input
        type="checkbox"
        id="terms-acceptance"
        className="w-4 h-4 mt-0.5 rounded border-slate-300 text-primary-500 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-describedby="terms-acceptance-label"
      />
      <label
        id="terms-acceptance-label"
        htmlFor="terms-acceptance"
        className={`text-sm text-slate-600 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
      >
        J'accepte les{' '}
        <a
          href={TERMS_LINKS.terms}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          conditions d'utilisation
        </a>{' '}
        et la{' '}
        <a
          href={TERMS_LINKS.privacy}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          politique de confidentialité
        </a>
      </label>
    </div>
  );
}

export default TermsAcceptance;
